import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export const useWebhookStatus = () => {
  const { user } = useAuth();
  const [isTesting, setIsTesting] = useState(false);

  // Get Stripe integration ID
  const { data: integration } = useQuery({
    queryKey: ['stripe-integration'],
    queryFn: async () => {
      if (!user) return null;

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (!userRoles) return null;

      const { data } = await supabase
        .from('integrations')
        .select('id, webhook_secret, last_sync_at')
        .eq('provider', 'stripe')
        .eq('tenant_id', userRoles.tenant_id)
        .eq('status_active', true)
        .limit(1)
        .single();

      return data;
    },
    enabled: !!user,
  });

  // Get last webhook log
  const { data: lastLog, refetch: refetchLogs } = useQuery({
    queryKey: ['webhook-last-log', integration?.id],
    queryFn: async () => {
      if (!integration?.id) return null;

      const { data } = await supabase
        .from('integration_logs')
        .select('*')
        .eq('integration_id', integration.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      return data;
    },
    enabled: !!integration?.id,
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  const testWebhook = async () => {
    setIsTesting(true);
    try {
      // For now, just refresh the logs to check status
      await refetchLogs();
      return { success: true };
    } catch (error) {
      console.error('Error testing webhook:', error);
      return { success: false, error };
    } finally {
      setIsTesting(false);
    }
  };

  const getWebhookUrl = () => {
    if (!integration?.id) return null;
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${baseUrl}/functions/v1/webhook-stripe?integration_id=${integration.id}`;
  };

  const isWorking = lastLog?.status === 'success' && 
    lastLog?.created_at && 
    (new Date().getTime() - new Date(lastLog.created_at).getTime()) < 3600000; // Last hour

  return {
    integrationId: integration?.id,
    webhookUrl: getWebhookUrl(),
    lastEventDate: lastLog?.created_at,
    lastEventType: lastLog?.event_type,
    lastEventStatus: lastLog?.status,
    isWorking,
    isTesting,
    testWebhook,
    refetchLogs,
  };
};
